import createSmartTimePillData from './createSmartTimePillData';
import {
    scheduleMeeting,
    outlookMeeting,
    cortanaMeeting,
    cortanaMeetingMessage,
} from './getSmartTimeForItem.locstring.json';
import getSmartTimePropertyFromItem from './getSmartTimePropertyFromItem';
import getSmartTimeSuggestion from './getSmartTimeSuggestion';
import parseSmartTimeProperty from './parseSmartTimeProperty';
import { lazyGetItemProperties } from 'owa-get-item-manager';
import loc from 'owa-localize';
import { MailIcons } from 'owa-mail-icons';
import { lazyReplyByMeeting } from 'owa-mail-reading-pane-store';
import setCalendarInlineComposeViewState from 'owa-mail-reading-pane-store/lib/actions/setCalendarInlineComposeViewState';
import CalendarInlineComposeViewState from 'owa-mail-reading-pane-store/lib/store/schema/CalendarInlineComposeViewState';
import getConversationReadingPaneViewState from 'owa-mail-reading-pane-store/lib/utils/getConversationReadingPaneViewState';
import { FeatureNames } from 'owa-mail-smart-pill-features';
import {
    ClientItem,
    SmartTimeExtendedProperty,
    SmartTimeTrigger,
    SmartTimeSuggestion,
} from 'owa-mail-store';
import ExtendedPropertyType from 'owa-service/lib/contract/ExtendedPropertyType';
import Message from 'owa-service/lib/contract/Message';
import {
    initializeSmartPillLogData,
    SmartPillData,
    SmartPillLogData,
    logOnClickDatapoint,
    SMART_TIME_MENU_CLICKED,
} from 'owa-smart-pill';
import { GetItemManagerFeatureIds } from 'owa-mail-smart-pill-features';
import { trace } from 'owa-trace';
import { IContextualMenuItem, IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { lazyReplyWithText, ReplyWithTextFeatures } from 'owa-mail-message-actions';
import isCalendarHelpEnabled from './isCalendarHelpEnabled';
import { lazyShowCustomerFeedbackIntercept } from 'owa-intercept';

export default async function getSmartTimeForItem(item: ClientItem): Promise<SmartPillData[]> {
    let smartPillData: SmartPillData[] = [];

    if (item) {
        if (!item.SmartTimeExtendedProperty) {
            const getItemProperties = await lazyGetItemProperties.import();
            const itemWithProperty: ClientItem = (await getItemProperties(
                item.ItemId.Id,
                GetItemManagerFeatureIds.SmartTime
            )) as ClientItem;

            // At this point, if itemWithProperty exists, it should be the same as the item input into this function.
            if (itemWithProperty) {
                const extendedProperty: ExtendedPropertyType = getSmartTimePropertyFromItem(
                    itemWithProperty
                );

                parseSmartTimeProperty(itemWithProperty, extendedProperty);
            }
        }

        // It is possible email props for SmartTimes are unparsable. Null check to be sure.
        const smartTimeExtendedProp: SmartTimeExtendedProperty = item.SmartTimeExtendedProperty;

        if (smartTimeExtendedProp) {
            const entityId: string = smartTimeExtendedProp.entityId;
            const confidenceScore: number = smartTimeExtendedProp.confidenceScore * 100;
            const scheduler: SmartTimeTrigger = smartTimeExtendedProp.scheduler;
            const rawConfidenceScores = smartTimeExtendedProp.rawConfidenceScores;

            let smartTimeSuggestion: SmartTimeSuggestion = getSmartTimeSuggestion(item);
            let calendarInlineComposeViewState: CalendarInlineComposeViewState = null;

            // set meeting time in calendar inline compose view state if smartTimeSuggestion valid.
            if (smartTimeSuggestion) {
                await setCalendarInlineComposeViewState(item, smartTimeSuggestion);
                calendarInlineComposeViewState = getConversationReadingPaneViewState(
                    item.ConversationId?.Id
                ).calendarInlineComposeViewState;
            }

            const messageItem: Message = item as Message;
            const calendarHelpEnabled = isCalendarHelpEnabled();

            const logData: SmartPillLogData = initSmartTimeLogData(
                entityId,
                confidenceScore,
                scheduler,
                rawConfidenceScores,
                messageItem,
                smartTimeSuggestion,
                calendarHelpEnabled
            );

            let menuProps: IContextualMenuProps = null;
            if (calendarHelpEnabled) {
                menuProps = {
                    items: buildMenuItemForMeetingScheduler(
                        item,
                        calendarInlineComposeViewState,
                        entityId,
                        messageItem,
                        logData
                    ),
                };
            }

            //insert the "Schedule meeting" option
            smartPillData.push(
                createSmartTimePillData(
                    smartPillData.length /* index */,
                    loc(scheduleMeeting),
                    () =>
                        scheduleAMeetingWithOutlookCalendar(
                            item,
                            calendarInlineComposeViewState,
                            entityId,
                            messageItem,
                            { SuggestionsClickedTime: new Date().toISOString() }
                        ),
                    logData,
                    confidenceScore,
                    {
                        iconName: MailIcons.DateTime2,
                    },
                    menuProps
                )
            );
        }
    } else {
        trace.info('[getSmartTimesForItem] No item, cannot produce smart times.');
    }

    return smartPillData;
}

function initSmartTimeLogData(
    entityId: string,
    confidenceScore: number,
    scheduler: SmartTimeTrigger,
    rawConfidenceScores: any,
    messageItem: Message,
    smartTimeSuggestion?: SmartTimeSuggestion,
    calendarHelpEnabled?: boolean
): SmartPillLogData {
    const logData: SmartPillLogData = initializeSmartPillLogData(
        FeatureNames.SmartTime /* feature name */,
        loc(scheduleMeeting) /* loggable text */,
        entityId /* entity id */,
        confidenceScore /* confidence score */
    );

    const hasSuggestedTime: boolean = smartTimeSuggestion != null;
    const numOfToRecipient: number = !!messageItem.ToRecipients
        ? messageItem.ToRecipients.length
        : 0;
    const numOfCCRecipient: number = !!messageItem.CcRecipients
        ? messageItem.CcRecipients.length
        : 0;

    logData.mainPayload.rawConfidenceScores = rawConfidenceScores;
    logData.mainPayload.scheduler = scheduler;
    logData.mainPayload.hasSuggestedTime = hasSuggestedTime;
    logData.mainPayload.NumToRecipient = numOfToRecipient;
    logData.mainPayload.NumCCRecipient = numOfCCRecipient;
    logData.mainPayload.isCalendarHelpEnabled = calendarHelpEnabled;
    logData.cosmosOnlyData = messageItem.InternetMessageId;

    if (hasSuggestedTime) {
        logData.mainPayload.suggestedStartTime = smartTimeSuggestion.startTime;
        logData.mainPayload.suggestedEndTime = smartTimeSuggestion.endTime;
    }

    return logData;
}

export function scheduleAMeetingWithOutlookCalendar(
    item: ClientItem,
    calendarInlineComposeViewState: CalendarInlineComposeViewState,
    entityId: string,
    messageItem: Message,
    sigsData?: { [key: string]: string }
): void {
    // Add event listener for schedule a meeting with Outlook in the deeplink popout.
    // The intercept toast will trigger after the post message from the listener and
    // fade in on the parent window.
    window.addEventListener('message', onCloseShowInterceptToast);

    lazyReplyByMeeting.importAndExecute(
        item.ItemId.Id /* reference item id */,
        calendarInlineComposeViewState /*calendar view state - populate calendar form with smart suggested data */,
        false /*should send draft message boolean flag */,
        null /*source of the action (default) */,
        entityId /*extraction source id associated with this smart component */,
        messageItem.InternetMessageId,
        sigsData /*sigsData*/
    );
}

export function scheduleAMeetingWithCortanaAssistant(item: ClientItem): void {
    lazyReplyWithText.importAndExecute(
        item.ItemId.Id,
        item.ConversationId.Id,
        loc(cortanaMeetingMessage),
        ReplyWithTextFeatures.SmartTimeWithCortana
    );
}

export function buildMenuItemForMeetingScheduler(
    item: ClientItem,
    calendarInlineComposeViewState: CalendarInlineComposeViewState,
    entityId: string,
    messageItem: Message,
    logData: SmartPillLogData
): IContextualMenuItem[] {
    const menuItems: IContextualMenuItem[] = [];
    const outlookMeetingKey: string = 'OutlookMeeting';
    const cortanaMeetingKey: string = 'CortanaMeeting';
    menuItems.push({
        key: outlookMeetingKey,
        text: loc(outlookMeeting),
        onClick: () => {
            logOnClickDatapoint(SMART_TIME_MENU_CLICKED, logData, { subtype: outlookMeetingKey });
            const timeClicked: string = new Date().toISOString();
            scheduleAMeetingWithOutlookCalendar(
                item,
                calendarInlineComposeViewState,
                entityId,
                messageItem,
                {
                    SuggestionsClickedTime: timeClicked,
                    IsSmartTimeSuggestionsClicked: !!timeClicked ? 'True' : 'False',
                }
            );
        },
    });
    menuItems.push({
        key: cortanaMeetingKey,
        text: loc(cortanaMeeting),
        onClick: () => {
            logOnClickDatapoint(SMART_TIME_MENU_CLICKED, logData, { subtype: cortanaMeetingKey });
            scheduleAMeetingWithCortanaAssistant(item);
        },
    });
    return menuItems;
}

const onCloseShowInterceptToast = (event: any) => {
    if (!event?.data) {
        return;
    }
    if (typeof event.data === 'string') {
        const childWindowMessage = JSON.parse(event.data);

        if (childWindowMessage && childWindowMessage.source === 'calDraftItem') {
            lazyShowCustomerFeedbackIntercept.importAndExecute({ featureName: 'SmartTime' });
        }
    }
};
